import { fetch } from 'whatwg-fetch';
import { isCloud } from '../helpers/isCloud';
import { HttpClient } from './httpClient';
export class StaffClient {
    getApiUrl() {
        return `https://api.${isCloud() ? 'test' : ''}kontur.ru/staff`;
    }
    authProvider(request) {
        request.credentials = 'include';
        return request;
    }
    constructor(options) {
        Object.defineProperty(this, "httpClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.httpClient = new HttpClient(fetch, options?.url || this.getApiUrl(), this.authProvider);
    }
    async getReference(type, signal) {
        const reference = await this.httpClient
            .get(`v1/references/${type}`)
            .withAbortSignal(signal)
            .json();
        return reference.items;
    }
    async getReferences(referenceKeys, signal) {
        const references = {
            banks: [],
            countries: [],
            driverLicenseCategories: [],
            educationLevels: [],
            identityDocuments: [],
            languages: [],
            languageSkills: [],
            maritalStatuses: [],
            militaryFitnessCategories: [],
            militaryRankGroups: [],
            militaryRanks: [],
            militarySpecializations: [],
            militaryStatuses: [],
            positionCategories: [],
            positionCategoryGroups: [],
            relationships: [],
            residentStatuses: [],
        };
        const result = await Promise.all(Object.keys(references).map((el) => {
            const key = el;
            if (!referenceKeys || referenceKeys.includes(key)) {
                return this.getReference(key, signal);
            }
            return [];
        }));
        return Object.keys(references).reduce((acc, cur, curIndex) => {
            const key = cur;
            return { ...acc, [key]: result[curIndex] };
        }, references);
    }
    getEmployees(employerId, query, signal) {
        const { activePeriod, ...rest } = { ...(query || {}) };
        const queryParams = {
            ...rest,
            'activePeriod.begin': activePeriod?.begin,
            'activePeriod.end': activePeriod?.end,
        };
        return this.httpClient
            .get(`v1/employers/${employerId}/employees`)
            .withQueryParams(queryParams)
            .withAbortSignal(signal)
            .json();
    }
    patchEmployees(employerId, patchesByEmployeesIds, signal) {
        return this.httpClient
            .patch(`v1/employers/${employerId}/employees`)
            .withJsonBody(patchesByEmployeesIds)
            .withAbortSignal(signal)
            .json();
    }
    getEmployee(employerId, employeeId, signal) {
        return this.httpClient.get(`v1/employers/${employerId}/employees/${employeeId}`).withAbortSignal(signal).json();
    }
    createEmployee(employerId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/employees`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    patchEmployee(employerId, employeeId, operations, signal) {
        return this.httpClient
            .patch(`v1/employers/${employerId}/employees/${employeeId}`)
            .withJsonBody(operations)
            .withAbortSignal(signal)
            .json();
    }
    saveEmployee(employerId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/employees/merge`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    async deleteEmployee(employerId, employeeId, signal) {
        await this.httpClient.delete(`v1/employers/${employerId}/employees/${employeeId}`).withAbortSignal(signal);
    }
    getClients(employerId, query, signal) {
        const { activePeriod, ...rest } = query;
        const queryParams = {
            ...rest,
            'activePeriod.begin': activePeriod?.begin || undefined,
            'activePeriod.end': activePeriod?.end || undefined,
        };
        return this.httpClient
            .get(`v1/employers/${employerId}/clients`)
            .withQueryParams(queryParams)
            .withAbortSignal(signal)
            .json();
    }
    createClient(employerId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/clients`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    patchClient(employerId, employeeId, operations, signal) {
        const supportedOperations = operations.map((value) => ({
            ...value,
            op: value.op === 'add' ? 'replace' : value.op,
        }));
        return this.httpClient
            .patch(`v1/employers/${employerId}/clients/${employeeId}`)
            .withJsonBody(supportedOperations)
            .withAbortSignal(signal)
            .json();
    }
    async deleteClient(employerId, employeeId, signal) {
        await this.httpClient.delete(`v1/employers/${employerId}/clients/${employeeId}`).withAbortSignal(signal);
    }
    getWorkingEvents(employerId, query = {}, signal) {
        const { period, sendPeriod, ...rest } = query;
        return this.httpClient
            .get(`v1/employers/${employerId}/workingEvents`)
            .withQueryParams({
            ...rest,
            'period.begin': period?.begin,
            'period.end': period?.end,
            'sendPeriod.begin': sendPeriod?.begin,
            'sendPeriod.end': sendPeriod?.end,
        })
            .withAbortSignal(signal)
            .json();
    }
    getWorkingEvent(employerId, employeeId, eventId, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/workingEvents/${employeeId}+${eventId}`)
            .withAbortSignal(signal)
            .json();
    }
    async deleteWorkingEvent(employerId, employeeId, eventId, signal) {
        await this.httpClient
            .delete(`v1/employers/${employerId}/workingEvents/${employeeId}+${eventId}`)
            .withAbortSignal(signal);
    }
    patchWorkingEvent(employerId, employeeId, eventId, operations, signal) {
        return this.httpClient
            .patch(`v1/employers/${employerId}/workingEvents/${employeeId}+${eventId}`)
            .withJsonBody(operations)
            .withAbortSignal(signal)
            .json();
    }
    createWorkingEvent(employerId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/workingEvents`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    mergeWorkingEvent(employerId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/workingEvents/merge`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    getIdentityDocument(employerId, employeeId, documentId, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/employees/${employeeId}/identityDocuments/${documentId}`)
            .withAbortSignal(signal)
            .json();
    }
    getIdentityDocuments(employerId, employeeId, query, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/employees/${employeeId}/identityDocuments`)
            .withQueryParams(query)
            .withAbortSignal(signal)
            .json();
    }
    getClientsIdentityDocuments(employerId, employeeId, query, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/clients/${employeeId}/identityDocuments`)
            .withQueryParams(query)
            .withAbortSignal(signal)
            .json();
    }
    createIdentityDocument(employerId, employeeId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/employees/${employeeId}/identityDocuments`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    createClientsIdentityDocument(employerId, employeeId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/clients/${employeeId}/identityDocuments`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    mergeIdentityDocument(employerId, employeeId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/employees/${employeeId}/identityDocuments/merge`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    async deleteIdentityDocument(employerId, employeeId, documentId, signal) {
        await this.httpClient
            .delete(`v1/employers/${employerId}/employees/${employeeId}/identityDocuments/${documentId}`)
            .withAbortSignal(signal);
    }
    async deleteClientsIdentityDocument(employerId, employeeId, documentId, signal) {
        await this.httpClient
            .delete(`v1/employers/${employerId}/clients/${employeeId}/identityDocuments/${documentId}`)
            .withAbortSignal(signal);
    }
    patchIdentityDocument(employerId, employeeId, documentId, operations, signal) {
        return this.httpClient
            .patch(`v1/employers/${employerId}/employees/${employeeId}/identityDocuments/${documentId}`)
            .withJsonBody(operations)
            .withAbortSignal(signal)
            .json();
    }
    patchClientsIdentityDocument(employerId, employeeId, documentId, operations, signal) {
        return this.httpClient
            .patch(`v1/employers/${employerId}/clients/${employeeId}/identityDocuments/${documentId}`)
            .withJsonBody(operations)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Запросить список документов */
    async getDocuments(employerId, query, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/documents`)
            .withQueryParams(query)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Запросить документ по type+originId */
    async getDocumentByTypeOriginId(employerId, type, originId, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/documents/${type}+${originId}`)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Запросить документ по documentId */
    async getDocumentById(employerId, documentId, signal) {
        return this.httpClient.get(`v1/employers/${employerId}/documents/${documentId}`).withAbortSignal(signal).json();
    }
    /** Добавить документ */
    async createDocument(employerId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/documents`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Удалить документ по type+originId */
    async deleteDocumentByTypeOriginId(employerId, type, originId, signal) {
        await this.httpClient
            .delete(`v1/employers/${employerId}/documents/${type}+${originId}`)
            .withAbortSignal(signal);
    }
    /** @description Удалить документ по documentId */
    async deleteDocumentById(employerId, documentId, signal) {
        await this.httpClient.delete(`v1/employers/${employerId}/documents/${documentId}`).withAbortSignal(signal);
    }
    /** @description Запросить родственника */
    async getRelative(employerId, employeeId, relativeId, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/employees/${employeeId}/relatives/${relativeId}`)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Удалить родственника */
    async deleteRelative(employerId, employeeId, relativeId, signal) {
        await this.httpClient
            .delete(`v1/employers/${employerId}/employees/${employeeId}/relatives/${relativeId}`)
            .withAbortSignal(signal);
    }
    /** @description Редактировать родственника */
    async patchRelative(employerId, employeeId, relativeId, operations, signal) {
        return this.httpClient
            .patch(`v1/employers/${employerId}/employees/${employeeId}/relatives/${relativeId}`)
            .withJsonBody(operations)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Запросить список родственников */
    async getRelatives(employerId, employeeId, query, signal) {
        return this.httpClient
            .get(`v1/employers/${employerId}/employees/${employeeId}/relatives`)
            .withQueryParams(query)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Добавить родственника */
    async addRelative(employerId, employeeId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/employees/${employeeId}/relatives`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Смержить родственника */
    async mergeRelative(employerId, employeeId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/employees/${employeeId}/relatives/merge`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
    /** @description Сопоставить сотрудникаа */
    async matchEmployee(employerId, request, signal) {
        return this.httpClient
            .post(`v1/employers/${employerId}/employees/match`)
            .withJsonBody(request)
            .withAbortSignal(signal)
            .json();
    }
}
